import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Button,
  Stack,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Refresh,
  Home,
  EmojiEvents,
  TrendingUp,
  QueryStats,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ComposedChart,
  Line,
} from 'recharts';

const PRIMARY = '#1a237e';
const SUCCESS = '#2e7d32';
const WARNING = '#ed6c02';
const ERROR = '#d32f2f';

const ECMCompliance = () => {
  const theme = useTheme();

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" color="textSecondary" fontWeight={600}>
            ECM Compliance KPI
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              bgcolor: PRIMARY,
              '&:hover': { bgcolor: alpha(PRIMARY, 0.8) },
            }}
          >
            Historical Data
          </Button>
          <IconButton size="small" sx={{ bgcolor: 'white' }}>
            <Refresh fontSize="small" />
          </IconButton>
          <IconButton size="small" sx={{ bgcolor: 'white' }}>
            <Home fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Top Row: Core KPI Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        <Card sx={{ borderTop: `5px solid ${ERROR}`, borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" fontWeight={600} color="textSecondary">
              Electronic %
            </Typography>
            <Typography variant="h3" fontWeight={700} color={ERROR}>
              93.70%
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: PRIMARY, py: 0.5, px: 2 }}>
            <Typography variant="subtitle2" color="white" fontWeight={600} align="center">
              ECM Method
            </Typography>
          </Box>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Electronic
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                2,530 (93.7%)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="textSecondary">
                Manual
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                170 (6.3%)
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" fontWeight={600} color="textSecondary">
              Geo-Fence Breaches
            </Typography>
            <Typography variant="h3" fontWeight={700} color={PRIMARY}>
              1,047
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: alpha(SUCCESS, 0.08), borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <TrendingUp sx={{ color: SUCCESS }} />
              <Typography variant="subtitle2" color={SUCCESS} fontWeight={600}>
                AI Growth Forecast
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight={800} color={SUCCESS}>
              +2.4%
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Predicted for next cycle
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Middle Section: Duration & Punctuality */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Compliance by Duration */}
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ bgcolor: PRIMARY, p: 1, mb: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" color="white" fontWeight={600}>
              ECM Compliance by Duration
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '5fr 7fr' },
              gap: 2,
            }}
          >
            <Box>
              {durationData.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1.5,
                    pb: 0.5,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    {item.label}
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {item.value} | {item.perc}%
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ height: 150 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={durationData} margin={{ left: -30 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="label" hide />
                  <Bar dataKey="perc" radius={[0, 5, 5, 0]}>
                    {durationData.map((entry) => (
                      <Cell key={entry.label} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Paper>

        {/* Compliance by Punctuality */}
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ bgcolor: PRIMARY, p: 1, mb: 2, borderRadius: 1 }}>
            <Typography variant="subtitle2" color="white" fontWeight={600}>
              ECM Compliance by Punctuality
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '5fr 7fr' },
              gap: 2,
            }}
          >
            <Box>
              {punctualityData.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 1.5,
                    pb: 0.5,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    {item.label}
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {item.value} | {item.perc}%
                  </Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ height: 150 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={punctualityData} margin={{ left: -30 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="label" hide />
                  <Bar dataKey="perc" radius={[0, 5, 5, 0]}>
                    {punctualityData.map((entry) => (
                      <Cell key={entry.label} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Performance Trend & Above & Beyond */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '8fr 4fr' },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Trend Chart */}
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Electronic % Trend (Last 7 Days)
          </Typography>
          <Box sx={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis domain={[90, 100]} tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Bar dataKey="value" fill={WARNING} radius={[5, 5, 0, 0]} />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={PRIMARY}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Above & Beyond Performers */}
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha('#fbc02d', 0.06),
            border: `1px dashed ${WARNING}`,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <EmojiEvents sx={{ color: WARNING }} />
            Above & Beyond Performers
          </Typography>
          <Box sx={{ mt: 2 }}>
            {topPerformers.map((staff) => (
              <Box
                key={staff.name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: 12,
                      bgcolor: SUCCESS,
                    }}
                  >
                    {staff.name[0]}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {staff.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {staff.commendations} Commendations
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Top Tier"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            ))}
          </Box>
          <Button
            fullWidth
            variant="text"
            size="small"
            startIcon={<QueryStats />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            View Excellence Report
          </Button>
        </Paper>
      </Box>

      {/* Employee Compliance Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Staff Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Electronic Compliance</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Punctuality</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Duration Accuracy</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Integrity Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((row) => (
              <TableRow key={row.name} hover>
                <TableCell>
                  <Typography fontWeight={600}>{row.name}</Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography>{row.comp}%</Typography>
                    {row.comp >= 90 ? (
                      <CheckCircle sx={{ fontSize: 16, color: SUCCESS }} />
                    ) : row.comp >= 75 ? (
                      <Warning sx={{ fontSize: 16, color: WARNING }} />
                    ) : (
                      <ErrorIcon sx={{ fontSize: 16, color: ERROR }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.punc}
                    size="small"
                    color={row.punc === 'On Time' ? 'success' : 'error'}
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>{row.dur}%</TableCell>
                <TableCell sx={{ minWidth: 120 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={row.score}
                      sx={{
                        flex: 1,
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#eee',
                      }}
                    />
                    <Typography variant="caption" fontWeight={600}>
                      {row.score}%
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Data
const durationData = [
  { label: 'Compliant', value: 1810, perc: 71.54, color: SUCCESS },
  { label: 'Overstay', value: 459, perc: 18.14, color: WARNING },
  { label: 'Understay', value: 261, perc: 10.32, color: ERROR },
];

const punctualityData = [
  { label: 'On Time', value: 1602, perc: 63.32, color: SUCCESS },
  { label: 'Early', value: 566, perc: 22.37, color: WARNING },
  { label: 'Late', value: 362, perc: 14.31, color: ERROR },
];

const trendData = [
  { name: 'Fri 30/01', value: 95.1 },
  { name: 'Sat 31/01', value: 94.1 },
  { name: 'Sun 01/02', value: 93.1 },
  { name: 'Mon 02/02', value: 95.7 },
  { name: 'Tue 03/02', value: 95.1 },
  { name: 'Wed 04/02', value: 95.3 },
  { name: 'Thu 05/02', value: 93.7 },
];

const topPerformers = [
  { name: 'Rachel Douglas', commendations: 12 },
  { name: 'Paula Neil', commendations: 9 },
];

const tableRows = [
  { name: 'Sarah Jenkins', comp: 99.2, punc: 'On Time', dur: 98, score: 99 },
  { name: 'David Miller', comp: 85.4, punc: 'Late', dur: 70, score: 75 },
];

export default ECMCompliance;